import { Navigate, Outlet, RouteObject } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import { fetchNavigation } from '../api/portalGateway/navigation';
import { InactivityObserver } from '../components/inactivityobserver/InactivityObserver';
import { LegacyUrlHandler } from '../components/legacyurlhandler/LegacyUrlHandler';
import { UserRolesLoader } from '../components/userrolesloader/UserRolesLoader';
import { ChangePassword as ChangePasswordScene } from '../scenes/ChangePassword';
import { Disclaimer } from '../scenes/Disclaimer';
import { Login as LoginScene } from '../scenes/Login';
import { Main as MainScene } from '../scenes/Main';
import { useStore } from '../store';
import { prependRoleId } from '../utils/prependRoleId';
import { pageWithRoleLink } from '../utils/urlPath';

const RequireLogin = ({
  changePasswordCheck = true,
}: {
  changePasswordCheck?: boolean;
}) => {
  const [isAuthTokenExpired, session] = useStore(
    useShallow((state) => [
      state.session?.isAuthTokenExpired() ?? true,
      state.session,
    ])
  );

  if (!session || isAuthTokenExpired) {
    return <Navigate to="/login" />;
  }

  if (changePasswordCheck && session?.hasToChangePassword()) {
    return <Navigate to="/changeInitialPassword" />;
  }

  return <Outlet />;
};

function NavigateToFirstPage() {
  const { data: navigationData } = useQuery({
    queryKey: prependRoleId(['navigation']),
    queryFn: fetchNavigation,
  });

  const role = useStore((state) => state.activeUserRole);

  return navigationData != null && role != null ? (
    <Navigate
      to={pageWithRoleLink(role.id, {
        slug: navigationData.data.initialPageSlug,
      })}
    />
  ) : (
    <MainScene pageType="page" />
  );
}

export const routes = (isLoggedIn: boolean): RouteObject[] => [
  // login
  {
    path: '/login',
    element: isLoggedIn ? <Navigate to="/" /> : <LoginScene />,
  },
  {
    path: '/changeInitialPassword',
    element: <RequireLogin changePasswordCheck={false} />,
    children: [
      { index: true, element: <ChangePasswordScene forcedChange={true} /> },
    ],
  },
  {
    path: '/changePassword',
    element: <RequireLogin />,
    children: [
      { index: true, element: <ChangePasswordScene forcedChange={false} /> },
    ],
  },
  // routes requiring login
  {
    path: '/',
    element: <RequireLogin />,
    children: [
      {
        element: <InactivityObserver />,
        children: [
          {
            element: <Disclaimer />,
            children: [
              {
                element: <LegacyUrlHandler />,
                children: [
                  {
                    element: <UserRolesLoader />,
                    children: [
                      {
                        index: true,
                        element: <NavigateToFirstPage />,
                      },
                      {
                        path: '/p',
                        element: <NavigateToFirstPage />,
                      },
                      {
                        path: '/p/:roleId',
                        element: <NavigateToFirstPage />,
                      },
                      {
                        path: '/p/:roleId/*',
                        element: <MainScene pageType="page" />,
                      },
                      {
                        path: '/dp/:roleId/*',
                        element: <MainScene pageType="detailPage" />,
                      },
                      { path: '*', element: <RequireLogin /> },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  // 404
  { path: '*', element: <div>NOT FOUND</div> },
];
